import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ClipboardList, Inbox, ShoppingCart, Package, Store, LogOut, Menu, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EPLogo, CustomerBadge } from "@/components/Brand";
import { Sheet, SheetContent } from "@/components/ui/sheet";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/orders", label: "Order History", icon: ClipboardList },
  { to: "/approvals", label: "Web Order Approvals", icon: Inbox, badge: true },
  { to: "/new-order", label: "New Order", icon: ShoppingCart },
  { to: "/products", label: "Product Catalogue", icon: Package },
  { to: "/locations", label: "Stores & Concessions", icon: Store },
];

const NavItems = ({ onNavigate }) => {
  const { meta } = useAuth();
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.map(({ to, label, icon: Icon, badge }) => (
        <NavLink key={to} to={to} onClick={onNavigate} data-testid={`nav-${to.slice(1)}`}
          className={({ isActive }) => `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
          {({ isActive }) => (<>
            <span className={`h-5 w-0.5 rounded-full transition-colors ${isActive ? "bg-[#FF0000]" : "bg-transparent"}`} />
            <Icon className="h-4 w-4" />
            <span className="flex-1">{label}</span>
            {badge && meta?.pending_count > 0 && (
              <span className="rounded-full bg-[#FF0000] px-2 py-0.5 text-[10px] font-bold text-white" data-testid="nav-pending-count">{meta.pending_count}</span>
            )}
          </>)}
        </NavLink>
      ))}
    </nav>
  );
};

const Sidebar = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="grain relative flex h-full flex-col bg-[#111214] text-white">
      <div className="border-b border-white/10 px-5 pb-5 pt-6"><EPLogo className="h-9" /><div className="mt-4 h-0.5 w-10 bg-[#FF0000]" /></div>
      <div className="px-6 pb-4 pt-6 label-xs !text-slate-500">Harrods Account</div>
      <NavItems onNavigate={onNavigate} />
      <a href="/EP_Group_Harrods_User_Guide.pdf" target="_blank" rel="noreferrer" data-testid="nav-user-guide"
        className="mx-3 mt-4 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-white">
        <span className="h-5 w-0.5" /><BookOpen className="h-4 w-4" /><span>User Guide (PDF)</span>
      </a>
      <div className="mt-auto border-t border-white/10 p-4">
        <div className="mb-3 px-2">
          <div className="text-sm font-semibold" data-testid="sidebar-user-name">{user.name}</div>
          <div className="text-xs text-slate-400" data-testid="sidebar-user-role">{user.role_label} · {user.scope_label}</div>
        </div>
        <button data-testid="switch-role-btn" onClick={async () => { await logout(); nav("/login"); }}
          className="flex w-full items-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm text-slate-300 transition-colors hover:border-[#FF0000] hover:text-white">
          <LogOut className="h-4 w-4" /> Switch role / sign out
        </button>
      </div>
    </div>
  );
};

export default function Layout() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen lg:pl-64">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block"><Sidebar /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 border-0 p-0"><Sidebar onNavigate={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b bg-[#F6F5F1]/85 px-4 py-3 backdrop-blur-xl sm:px-8">
        <button className="rounded-md p-2 hover:bg-black/5 lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
        <div className="min-w-0 flex-1">
          <div className="label-xs">EP Group · Harrods Management</div>
          <div className="truncate text-sm text-slate-600" data-testid="header-scope">Viewing as <b className="text-slate-900">{user.role_label}</b> — {user.scope_label}</div>
        </div>
        <CustomerBadge />
      </header>
      <main className="px-4 py-8 sm:px-8"><Outlet /></main>
    </div>
  );
}
